var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Exclude, Expose, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsDateString, IsEnum, IsNumber, IsObject, IsOptional, IsString, IsUUID, } from 'class-validator';
import { CallPatientStatus } from '../../calls/enums/call-patient.enum.js';
import { TreatmentLocation } from '../../calls/enums/treatment-location.enum.js';
import { OfficeLocationFullSizeDto } from '../../offices/index.js';
import { Role } from '../../roles/enums/role.enum.js';
import { UserProfileDto } from '../../user/dto/user.profile.dto.js';
import { AppointmentOrigin } from '../enums/appointment-origin.enum.js';
import { AppointmentStatus } from '../enums/appointment-status.enum.js';
import { CancellationOrigin } from '../enums/cancellation-origin.enum.js';
import { PracticeConfirmation } from '../enums/practice-confirmation.enum.js';
import { AppointmentParticipantResponseDto } from './appointment-participant.response.dto.js';
import { PatientAppointmentActionsDto } from './patient-appointment-actions.dto.js';
export class AppointmentBaseDto {
    id;
    createdAt;
    updatedAt;
    deletedAt;
    scheduledStart;
    plannedDuration;
    treatmentLocation;
    status;
    service;
    // `description` is therapist free-text with no patient-safe convention, so
    // it is restricted to office roles (TM-9436 / #1331). `roomId`, `deviceIds`,
    // `seriesId`, `occurrenceKey`, `ruleVersion` and `officeProductAssignmentId`
    // below are internal scheduling metadata with no patient rendering — kept
    // office-only for the same reason.
    description;
    roomId;
    deviceIds = [];
    therapistId;
    officeLocationId;
    officeLocation;
    officeProductAssignmentId;
    /**
     * Booking provenance and vetting state (PRD #1343). Patient-visible so the
     * Terminübersicht can render a pending hold as "angefragt" and the practice
     * calendar can badge it — both read `practiceConfirmation === PENDING`.
     * `origin` distinguishes a patient self-booking from a practice booking.
     */
    origin = AppointmentOrigin.PRACTICE;
    practiceConfirmation = PracticeConfirmation.CONFIRMED;
    /**
     * When this hold is a reschedule, the id of the original appointment it will
     * replace once the practice confirms the move (PRD #1343). Office-only
     * scheduling metadata. Null for first bookings.
     */
    rescheduleOfAppointmentId = null;
    /**
     * Snapshot of the patient-entered intake (Heilmittel, counts, frequency,
     * urgency) for first-contact bookings (PRD #1343). Office-only — the front
     * desk reads it at confirm to prefill the treatment-plan form. Null for
     * plan-backed bookings, which carry no intake.
     */
    intakePayload = null;
    /**
     * Groups the holds a patient placed in one multi-slot pass (PRD #1343,
     * RS-004), so the practice inbox can render the pass as a single entry and
     * confirm/decline it as a unit. Office-only scheduling metadata. Null for
     * single (RS-001) and practice-booked appointments.
     */
    bookingPassId = null;
    /**
     * When the booker's identity/contact was verified (PRD #1358, RS-004). Set at
     * creation for practice bookings and authenticated in-app holds; NULL for a
     * website hold until the visitor clicks the email confirmation link. Office
     * calendars read `verifiedAt === null` to render a website hold as awaiting
     * verification; the pending-hold inbox shows only verified holds. Office-only.
     */
    verifiedAt = null;
    icsUid;
    icsSequence;
    /**
     * Series-membership metadata. `seriesId` is set when this row was
     * materialized from an `AppointmentSeries`; `occurrenceKey` is the
     * RFC 5545 RECURRENCE-ID (the original scheduled-start as a local-time
     * ISO string); `ruleVersion` is a snapshot of the series's `ruleVersion`
     * at materialization (mismatch with the series's current `ruleVersion`
     * indicates an "orphaned override").
     */
    seriesId;
    occurrenceKey;
    ruleVersion;
    callId;
    therapist;
    participants;
    /**
     * The requesting patient's OWN participation status on this appointment
     * (TM-9436 / #1331). The full `participants` array stays office-only so a
     * patient never sees co-participants in a group appointment; this scalar
     * opens exactly the one thing they may read — their own status. Populated
     * server-side by the patient appointment feed; null everywhere else.
     */
    myParticipantStatus = null;
    /**
     * Who cancelled the requesting patient's own participation (TM-9737), so their
     * overview can tell their own Absage from a Praxisabsage. Populated, like
     * `myParticipantStatus`, by the patient appointment feed only; null everywhere else.
     */
    myCancellationOrigin = null;
    /**
     * What the requesting patient may do with this appointment right now
     * (TM-9436 / #1334) — computed server-side by the single `PatientActionPolicy`
     * so the frontend affordance and the backend guard never disagree. Populated
     * only by the patient appointment feed; null everywhere else.
     */
    patientActions = null;
    // Computed properties
    get therapistOfficeId() {
        return this.therapist?.officeId;
    }
    set therapistOfficeId(_) {
        /* computed from therapist */
    }
    get patientIds() {
        return this.participants
            ? this.participants.map((p) => p?.patient?.id).filter((id) => !!id)
            : [];
    }
    set patientIds(_) {
        /* computed from participants */
    }
    get totalPatients() {
        return this.participants?.length || 0;
    }
    set totalPatients(_) {
        /* computed from participants */
    }
    get declines() {
        return this.participants?.filter((p) => p.status === CallPatientStatus.DECLINED).length || 0;
    }
    set declines(_) {
        /* computed from participants */
    }
    get isDeclined() {
        return this.totalPatients > 0 && this.declines === this.totalPatients;
    }
    set isDeclined(_) {
        /* computed from participants */
    }
    get acceptations() {
        return this.participants?.filter((p) => p.status === CallPatientStatus.ACCEPTED).length || 0;
    }
    set acceptations(_) {
        /* computed from participants */
    }
    get isAccepted() {
        return this.totalPatients > 0 && this.acceptations === this.totalPatients;
    }
    set isAccepted(_) {
        /* computed from participants */
    }
    get pendings() {
        return (this.participants?.filter((p) => [CallPatientStatus.PENDING].includes(p.status)).length || 0);
    }
    set pendings(_) {
        /* computed from participants */
    }
    get isPending() {
        return this.pendings >= 1 && this.pendings + this.declines === this.totalPatients;
    }
    set isPending(_) {
        /* computed from participants */
    }
    isDoneAt(now) {
        if (!this.scheduledStart || this.plannedDuration == null)
            return false;
        const startMs = this.scheduledStart instanceof Date
            ? this.scheduledStart.getTime()
            : new Date(this.scheduledStart).getTime();
        if (isNaN(startMs))
            return false;
        const endTime = new Date(startMs + this.plannedDuration * 60_000);
        return endTime < now;
    }
    get isDone() {
        return this.isDoneAt(new Date());
    }
    set isDone(_) {
        /* computed from scheduledStart and plannedDuration */
    }
}
__decorate([
    IsUUID(),
    Expose(),
    __metadata("design:type", String)
], AppointmentBaseDto.prototype, "id", void 0);
__decorate([
    IsDateString(),
    Exclude(),
    __metadata("design:type", Date)
], AppointmentBaseDto.prototype, "createdAt", void 0);
__decorate([
    IsDateString(),
    Exclude(),
    __metadata("design:type", Date)
], AppointmentBaseDto.prototype, "updatedAt", void 0);
__decorate([
    IsDateString(),
    IsOptional(),
    Exclude(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "deletedAt", void 0);
__decorate([
    IsDateString(),
    Type(() => Date),
    Expose(),
    __metadata("design:type", Date)
], AppointmentBaseDto.prototype, "scheduledStart", void 0);
__decorate([
    IsNumber(),
    Expose(),
    __metadata("design:type", Number)
], AppointmentBaseDto.prototype, "plannedDuration", void 0);
__decorate([
    IsEnum(TreatmentLocation),
    Expose(),
    __metadata("design:type", String)
], AppointmentBaseDto.prototype, "treatmentLocation", void 0);
__decorate([
    IsEnum(AppointmentStatus),
    Expose(),
    __metadata("design:type", String)
], AppointmentBaseDto.prototype, "status", void 0);
__decorate([
    IsString(),
    Expose(),
    IsOptional(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "service", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "description", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "roomId", void 0);
__decorate([
    IsArray(),
    IsUUID('4', { each: true }),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Array)
], AppointmentBaseDto.prototype, "deviceIds", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "therapistId", void 0);
__decorate([
    IsOptional(),
    IsUUID(),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "officeLocationId", void 0);
__decorate([
    IsOptional(),
    IsObject(),
    Type(() => OfficeLocationFullSizeDto),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "officeLocation", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "officeProductAssignmentId", void 0);
__decorate([
    IsEnum(AppointmentOrigin),
    Expose(),
    __metadata("design:type", String)
], AppointmentBaseDto.prototype, "origin", void 0);
__decorate([
    IsEnum(PracticeConfirmation),
    Expose(),
    __metadata("design:type", String)
], AppointmentBaseDto.prototype, "practiceConfirmation", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "rescheduleOfAppointmentId", void 0);
__decorate([
    IsObject(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "intakePayload", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "bookingPassId", void 0);
__decorate([
    IsDateString(),
    IsOptional(),
    Type(() => Date),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "verifiedAt", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Exclude(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "icsUid", void 0);
__decorate([
    IsNumber(),
    IsOptional(),
    Exclude(),
    __metadata("design:type", Number)
], AppointmentBaseDto.prototype, "icsSequence", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "seriesId", void 0);
__decorate([
    IsString(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "occurrenceKey", void 0);
__decorate([
    IsNumber(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "ruleVersion", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "callId", void 0);
__decorate([
    IsOptional(),
    IsObject(),
    Type(() => UserProfileDto),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "therapist", void 0);
__decorate([
    IsArray(),
    Type(() => AppointmentParticipantResponseDto),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Array)
], AppointmentBaseDto.prototype, "participants", void 0);
__decorate([
    IsEnum(CallPatientStatus),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "myParticipantStatus", void 0);
__decorate([
    IsEnum(CancellationOrigin),
    IsOptional(),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "myCancellationOrigin", void 0);
__decorate([
    IsOptional(),
    IsObject(),
    Type(() => PatientAppointmentActionsDto),
    Expose(),
    __metadata("design:type", Object)
], AppointmentBaseDto.prototype, "patientActions", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [Object])
], AppointmentBaseDto.prototype, "therapistOfficeId", null);
__decorate([
    IsArray(),
    IsString({ each: true }),
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [Array])
], AppointmentBaseDto.prototype, "patientIds", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsNumber(),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], AppointmentBaseDto.prototype, "totalPatients", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsNumber(),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], AppointmentBaseDto.prototype, "declines", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsBoolean(),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], AppointmentBaseDto.prototype, "isDeclined", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsNumber(),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], AppointmentBaseDto.prototype, "acceptations", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsBoolean(),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], AppointmentBaseDto.prototype, "isAccepted", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsNumber(),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], AppointmentBaseDto.prototype, "pendings", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsBoolean(),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], AppointmentBaseDto.prototype, "isPending", null);
__decorate([
    Expose({ groups: [Role.ADMIN, Role.MANAGEMENT, Role.THERAPIST] }),
    IsBoolean(),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], AppointmentBaseDto.prototype, "isDone", null);
//# sourceMappingURL=appointment.base.dto.js.map